import { AssetSet, ResourceTypeToConfigKeyMap } from '@nova/configs';
import { EResourceType, IConfigResourceRef, ResourceListByType } from '@nova/types';
import { Logger } from '@nova/utils';
import { Assets, extensions, UnresolvedAsset } from 'pixi.js';

import { AssetUtils } from './AssetUtils';
import { loadBlobTexture } from './loaders/BlobTextureLoader';
import { extendedSpritesheetAsset } from './loaders/ExtendedSpriteSheetLoader';
import { loadWebFile } from './loaders/WebFileLoader';

export interface IAssetOptions {
  baseURL?: string;
  mapAssetURL?: string;
  assetSet?: AssetSet;
}

export class AssetManager {
  public static BASE_URL: string;
  /**
   * Base URL for tiled map images when loading from CDN (ParseTileSets / TiledParser build `src` as prefix+path).
   * Extension-prepared maps rely on textures already cached under the same path strings as in map JSON
   * (see ParseTileSets.isAssetAlreadyAvailable second branch); no scene-level override of this field is required.
   */
  public static MAP_ASSET_URL: string;

  public static readonly DEFAULT_BASE_URL = 'https://staticcontent.knowledgehook.com/content/khg';

  private static _cachedAssets?: AssetSet;

  /**
   * Initializes the asset cache with a manifest fetched from remote.
   * Begins to download manifest bundles in the background.
   *
   * @param assetsInstance - The PIXI.Assets instance to initialize
   * @param manifestURL - An optional url pointing to the manifest to initialize with
   */
  public static async initialize(options?: IAssetOptions): Promise<void> {
    AssetManager.registerAssetExtensions();

    this.BASE_URL = options?.baseURL || this.DEFAULT_BASE_URL;
    this.MAP_ASSET_URL = options?.mapAssetURL || `${this.BASE_URL}/maps/v9/`;

    this._cachedAssets = options?.assetSet;

    await AssetUtils.initializeAssets();

    for (const bundle of options?.assetSet?.pixiManifest.bundles || []) {
      if (Assets.resolver.hasBundle(bundle.name)) {
        continue;
      }
      Assets.addBundle(bundle.name, bundle.assets);
    }
  }

  /**
   * Registers custom extensions for loading assets
   */
  private static registerAssetExtensions(): void {
    extensions.add(extendedSpritesheetAsset);
    extensions.add(loadWebFile);
    extensions.add(loadBlobTexture);

    // Register spine loader if spine module is available
    this.registerSpineLoader();
  }

  /**
   * Registers the spine loader if the spine module has been imported
   */
  private static registerSpineLoader(): void {
    // Import and register the spine loader during AssetManager initialization
    import('../../spine/loader/CustomSpineTextureAtlasLoader').then((spineModule) => {
      extensions.add(spineModule.customSpineTextureAtlasLoader);
    }).catch(() => {
      Logger.info('Spine module not available, spine loader will not be registered');
    });
  }

  /**
   * Loads assets from an asset set into the asset manager.
   *
   * @param assetData - The assets to load, organized by resource type.
   * @returns A promise that resolves when the assets are loaded.
   */
  public static async loadAssetsFromAssetSet(assetData: ResourceListByType) {
    const assetsToLoad: UnresolvedAsset[] = [];
    for (const [resourceType, assetKeys] of Object.entries(assetData)) {
      for (const assetKey of assetKeys) {
        const type = <EResourceType>resourceType;
        const datasetKey = <ResourceTypeToConfigKeyMap[typeof type]>assetKey;
        if (Assets.cache.has(datasetKey)) {
          continue;
        }
        const assets = this.getSourceFromAssetSet({ type, datasetKey });
        for (const asset of assets) {
          assetsToLoad.push(asset);
        }
      }
    }
    await Assets.load(assetsToLoad);
  }

  public static getAssetEntry<T extends EResourceType, K extends keyof AssetSet[T]>(assetType: T, datasetKey: K): AssetSet[T][K] {
    if (!this._cachedAssets) {
      throw new Error('AssetManager not initialized with asset set');
    }

    const assetSet = this._cachedAssets[assetType];
    return assetSet[datasetKey];
  }

  public static getSourceFromAssetSet(resource: IConfigResourceRef): UnresolvedAsset[] {
    if (!this._cachedAssets) {
      throw new Error('AssetManager not initialized with asset set');
    }

    const originalPathUrl = 'https://staticcontent.knowledgehook.com/content/khg/';
    const pixiPathUrl = 'https://staticcontent.knowledgehook.com/content/khg/pixi/';

    let assetData: UnresolvedAsset[];

    switch (resource.type) {
      case EResourceType.image:
        assetData = [{
          alias: resource.datasetKey,
          src: (this._cachedAssets.image)[resource.datasetKey]
        }];
        break;
      case EResourceType.spritesheet:
        assetData = [{
          alias: resource.datasetKey,
          src: (this._cachedAssets.spritesheet)[resource.datasetKey].path
        }];
        break;
      case EResourceType.multiAtlas:
      {
        const multiAtlas = (this._cachedAssets.multiAtlas)[resource.datasetKey];
        const multiAtlasData = {
          clips: multiAtlas.clips,
          frames: multiAtlas.frames
        };

        // TODO: Update this when pixi assets are moved over
        const pixiPath = multiAtlas.path.replace(originalPathUrl, pixiPathUrl);
        assetData = [{
          alias: resource.datasetKey,
          src: `${pixiPath}/${multiAtlas.json}`,
          data: multiAtlasData
        }];
        break;
      }
      case EResourceType.spineAtlas:
      {
        const spineAtlas = (this._cachedAssets.spineAtlas)[resource.datasetKey];
        assetData = [{
          alias: `${resource.datasetKey}.atlas`,
          src: `${spineAtlas.path}/${spineAtlas.atlas}`
        }, {
          alias: `${resource.datasetKey}.json`,
          src: `${spineAtlas.path}/${spineAtlas.json}`
        }];
        break;
      }
      case EResourceType.audioTrack: {
        const audioTrack = (this._cachedAssets.audioTrack)[resource.datasetKey];
        assetData = [{
          alias: [`track.${resource.datasetKey}`, resource.datasetKey],
          src: `${this.BASE_URL}/${encodeURI(audioTrack.path)}`,
          data: audioTrack.data
        }];
      }
        break;
      default:
        throw new Error(`Resource type ${resource.type} not found in asset set`);
    }

    if (!assetData[0].src) {
      throw new Error(`Resource type ${resource.type} not found in asset set`);
    }

    return assetData;
  }
}
